import { NextPage } from 'next'
import { useLazyQuery } from '@apollo/client'
import styles from '../_artistPage.module.scss'
import { createContext, useEffect, useRef, useState } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faThLarge, faThList } from '@fortawesome/free-solid-svg-icons'
import classnames from 'classnames'
import ListSort from '../../../../components/ui/ListSort'
import { staticPath, staticProps } from '../../../../utils/createApolloClient'
import dynamic from 'next/dynamic'
import { SONGS_LIST_QUERY } from '../../../../gql/queries/songs_list_query'
import { ARTISTS_LIST_QUERY } from '../../../../gql/queries/artists_list_query'
import { ARTIST_DETAILS_QUERY } from '../../../../gql/queries/artist_details_query'

import ArtistLayout from '../components/ArtistLayout'
import { Artist, Song, SortSongsEnum } from '../../../../__generated__/graphql'
import useInView from 'react-cool-inview'

const SongsList = dynamic(() => import('../../../songs/Components/SongsList'))

export const SearchContext = createContext(null)

export interface FiltersState {
  orderBy: SortSongsEnum
  perPage: number
}

export const initalFiltersState: FiltersState = {
  orderBy: SortSongsEnum.CurrentlyTrending,
  perPage: 1000
}

const ArtistSongsPage: NextPage<{ artist: Artist; ssrSongs: Song[] }> = ({
  artist,
  ssrSongs
}) => {
  if (!artist) return <></>

  const [filtersState, setFiltersState] =
    useState<FiltersState>(initalFiltersState)
  const [view, setView] = useState('list')

  const [performSearch, { data: performSearchData }] =
    useLazyQuery(SONGS_LIST_QUERY)

  const initialRender = useRef(true)
  useEffect(() => {
    if (!initialRender.current)
      performSearch({
        variables: { artistIds: [artist.id], ...filtersState }
      })
    else initialRender.current = false
  }, [filtersState])

  const songs = (performSearchData?.songs || ssrSongs || []) as Song[]

  const { observe, inView } = useInView({
    onEnter: ({ unobserve }) => unobserve()
  })

  return (
    <ArtistLayout artist={artist}>
      <SearchContext.Provider
        value={{
          filtersState: filtersState,
          setFiltersState: setFiltersState
        }}
      >
        <header className={styles.tabsHeader}>
          <div className={classnames(styles.viewSelect)}>
            <div className="columns">
              <div className="column">
                <ListSort SearchContext={SearchContext} />
              </div>

              <div className={classnames(styles.noPaddingsColumn, 'column')}>
                <button
                  className={classnames(styles.tabLink, {
                    [styles.tabLinkActive]: view == 'list'
                  })}
                  onClick={() => setView('list')}
                >
                  <FontAwesomeIcon icon={faThList} size="2x" />
                </button>
              </div>

              <div className={classnames(styles.noPaddingsColumn, 'column')}>
                <button
                  className={classnames(styles.tabLink, {
                    [styles.tabLinkActive]: view == 'table'
                  })}
                  onClick={() => setView('table')}
                >
                  <FontAwesomeIcon icon={faThLarge} size="2x" />
                </button>
              </div>
            </div>
          </div>
        </header>

        <div className={styles.panel}>
          <div
            className={classnames(styles.songsListHolder, {
              [styles.tableView]: view == 'table'
            })}
          >
            <SongsList
              songs={songs.slice(0, 6)}
              isFlat={view == 'list'}
              inList={view == 'list'}
            />
            <div ref={observe}></div>
            {songs.length > 6 && inView && (
              <SongsList
                songs={songs.slice(6)}
                isFlat={view == 'list'}
                inList={view == 'list'}
              />
            )}
          </div>
        </div>
      </SearchContext.Provider>
    </ArtistLayout>
  )
}

export const getStaticPaths = staticPath({
  additionalPaths: async ({ client }) => {
    const { data } = await client.query({
      query: ARTISTS_LIST_QUERY
    })

    const paths = data.artists.map((artist) => ({
      params: { id: artist.id.toString() }
    }))

    return {
      paths,
      fallback: true
    }
  }
})

export const getStaticProps = staticProps({
  additionalProps: async ({ client, ctx }) => {
    const {
      data: { artist }
    } = await client.query({
      query: ARTIST_DETAILS_QUERY,
      variables: { id: Number(ctx.params.id) }
    })
    const {
      data: { songs: ssrSongs }
    } = await client.query({
      query: SONGS_LIST_QUERY,
      variables: {
        artistIds: [Number(ctx.params.id)],
        ...initalFiltersState
      }
    })
    return {
      artist,
      ssrSongs
    }
  }
})

export default ArtistSongsPage
